/**
 * Product Template Script
 * ------------------------------------------------------------------------------
 * A file that contains scripts highly couple code to the Product template.
 *
 * @namespace product
 */

import Variants from '../utils/theme-variants';
import { formatMoney } from '../utils/currency';
import sections from '../utils/theme-sections';
import adMindDetails from '../components/AdMind/detail';
import { handleWaitlistClick } from '../components/Waitlist';

const selectors = {
  addToCart: 'button[data-add-to-cart][type="submit"]',
  editCartItem: 'button[data-edit-cart-item][type="submit"]',
  addToCartText: '[data-add-to-cart-text]',
  editCartItemText: '[data-edit-cart-item-text]',
  comparePrice: '[data-compare-price]',
  comparePriceText: '[data-compare-text]',
  originalSelectorId: '[data-product-select]',
  priceWrapper: '[data-price-wrapper]',
  productImageWrapper: '[data-product-image-wrapper]',
  productFeaturedImage: '[data-product-featured-image]',
  productJson: '[data-product-json]',
  productPrice: '[data-product-price]',
  discountPercent: '[data-discount-percent]',
  productThumbs: '[data-product-single-thumbnail]',
  singleOptionSelector: '[data-single-option-selector]',
  editCartItemButtonContainer: '.ProductInfo__add-to-cart',
  pdpGrid: '.ProductGrid.PDP',
};

const cssClasses = {
  activeThumbnail: 'active-thumbnail',
  hide: 'hide',
};

let theme = window.theme || {};
let firstContainer = null;

/**
 * Product section constructor. Runs on page load as well as Theme Editor
 * `section:load` events.
 * @param {string} container - selector for the section container DOM element
 */

sections.register('product', {
  onLoad() {
    const productJsonElement = this.container.querySelector(selectors.productJson);
    if (!productJsonElement) {
      return;
    }

    const quickViewModal = document.querySelector('#QuickView.Modal--open');
    const isBuiltWithFastSimon = quickViewModal?.getAttribute('data-built-with-fastsimon') === 'true';

    if (isBuiltWithFastSimon) {
      return;
    }

    // we will prevent loading PDP section twice if it is already saved in firstContainer
    if (this.container === firstContainer) return;

    // if the first container is PDP grid, we will save it as firstContainer. Any other container will be ignored
    if (!firstContainer && this.container.matches(selectors.pdpGrid)) {
      firstContainer = this.container;
    }

    this.productSingleObject = JSON.parse(productJsonElement.innerHTML);

    const options = {
      container: this.container,
      enableHistoryState: true,
      singleOptionSelector: selectors.singleOptionSelector,
      originalSelectorId: selectors.originalSelectorId,
      product: this.productSingleObject,
    };

    this.settings = {};
    this.variants = new Variants(options);
    this.featuredImage = this.container.querySelector(selectors.productFeaturedImage);

    // save variant name
    window.variant_name = '';

    this.variants._updateHistoryState = this.updateHistory.bind(this);

    this.container.addEventListener('variantChange', this.updateAddToCartState.bind(this));
    this.container.addEventListener('variantPriceChange', this.updateProductPrices.bind(this));

    try {
      if (this.featuredImage) {
        this.container.addEventListener('variantImageChange', this.updateImages.bind(this));
      }
    } catch (err) {
      console.log(err);
      console.log('PDP: Error in featured image of product');
    }

    // Trigger GTM detail whenever you either open a new Quick View modal or PDP is loaded
    adMindDetails(this.variants);

    // Store inventory icon
    const disclaimerHover = document.querySelector('[data-inventory-disclaimer]');
    if (disclaimerHover) {
      disclaimerHover.addEventListener('click', (e) => {
        e.currentTarget.closest('[data-inventory-disclaimer-msg]').style.display = 'block';
      });
    }
  },

  updateHistory(variant) {
    if (window.location.href.indexOf('/products') === -1) {
      return;
    }

    if (!history.replaceState || !variant) {
      return;
    }
    const queryParams = window.location.search.split(/&(.*)/s);
    const updatedQueryParams = queryParams[1];
    let newurl = '';

    if (queryParams[0] && queryParams[0].indexOf('?variant') === -1) {
      if (queryParams[1]) {
        newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${
          variant.id
        }${queryParams[0].replace('?', '&')}&${queryParams[1]}`;
      } else {
        newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${
          variant.id
        }${queryParams[0].replace('?', '&')}`;
      }
    } else if (updatedQueryParams) {
      newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}&${updatedQueryParams}`;
    } else {
      newurl = `${window.location.protocol}//${window.location.host}${window.location.pathname}?variant=${variant.id}`;
    }

    window.history.replaceState(
      {
        path: newurl,
      },
      '',
      newurl,
    );
  },

  setActiveThumbnail(imageId) {
    let newImageId = imageId;

    // If "imageId" is not defined in the function parameter, find it by the current product image
    try {
      if (typeof newImageId === 'undefined') {
        const activeImage = this.container.querySelector(`${selectors.productImageWrapper}:not(.${cssClasses.hide})`);
        newImageId = activeImage ? activeImage.dataset.imageId : null;
      }
    } catch (err) {
      console.log(err);
      console.log('PDP: Error in image retrieval');
    }

    const thumbnail = this.container.querySelector(`${selectors.productThumbs}[data-thumbnail-id='${newImageId}']`);

    const thumbnails = this.container.querySelectorAll(selectors.productThumbs);
    thumbnails.forEach((thumb) => thumb.classList.remove(cssClasses.activeThumbnail));
    thumbnails.forEach((thumb) => thumb.removeAttribute('aria-current'));

    if (thumbnail) {
      thumbnail.classList.add(cssClasses.activeThumbnail);
      thumbnail.setAttribute('aria-current', true);
    }
  },

  switchImage(imageId) {
    const newImage = this.container.querySelector(`${selectors.productImageWrapper}[data-image-id='${imageId}']`);
    const otherImages = this.container.querySelectorAll(
      `${selectors.productImageWrapper}:not([data-image-id='${imageId}'])`,
    );
    if (newImage) {
      newImage.classList.remove(cssClasses.hide);
    }
    otherImages.forEach((image) => image.classList.add(cssClasses.hide));
  },

  /**
   * Set different attributes on either AddToCart or editCartItem buttons based on variant availability
   * @param {*} variant - The current variant object
   */
  updateAddToCartButtonByExisting(variant) {
    if (variant) {
      this.container.querySelector(selectors.priceWrapper).classList.remove(cssClasses.hide);
    } else {
      // We will go over the kind of button that is being used if it is 'add-to-cart' or 'edit-cart-item'
      // depending of this we will apply the correct changes to the elements
      const addToCartButton = this.container.querySelector(selectors.addToCart);
      const editCartItemButton = this.container.querySelector(selectors.editCartItem);

      if (addToCartButton) {
        addToCartButton.disabled = true;
        this.container.querySelector(selectors.addToCartText).innerHTML = theme.strings.unavailable;
      }
      if (editCartItemButton) {
        editCartItemButton.disabled = true;
        this.container.querySelector(selectors.editCartItemText).innerHTML = theme.strings.unavailable;
      }

      this.container.querySelector(selectors.priceWrapper).classList.add(cssClasses.hide);
      // This should replace the previous return statement
      throw new Error('PDP: no available variant');
    }
  },

  /**
   * Set different attributes on either AddToCart or editCartItem buttons based on variant inventory
   * @param {*} variant - The current variant object
   * @param {*} productJson - The current product object
   */
  updateAddToCartButtonByInventory(variant, productJson) {
    let hasInventory = variant.available;

    if (window.inventory980) {
      hasInventory = window.inventory980.color[variant.id];
    }

    const addToCartButton = this.container.querySelector(selectors.addToCart);
    const editCartItemButton = this.container.querySelector(selectors.editCartItem);
    const addToCartText = this.container.querySelector(selectors.addToCartText);
    const editCartItemText = this.container.querySelector(selectors.editCartItemText);
    const editCartItemButtonContainer = this.container.querySelector(selectors.editCartItemButtonContainer);

    if (hasInventory) {
      // Depending of the kind of button that is being used we will apply the correct changes to the elements
      if (addToCartButton) {
        addToCartButton.disabled = false;
        addToCartText.innerHTML = theme.strings.addToCart;
        addToCartText.setAttribute('title', 'Add to Bag');
        addToCartButton.setAttribute('data-waitlist-open', false);
        addToCartButton.setAttribute('data-add-to-cart', true);
      }
      if (editCartItemButton) {
        editCartItemButton.disabled = false;
        editCartItemText.innerHTML = 'Update Now';
        editCartItemText.setAttribute('title', 'Update Now');
        editCartItemButton.setAttribute('data-waitlist-open', false);
        editCartItemButton.setAttribute('data-edit-cart-item', true);
        editCartItemButtonContainer.setAttribute('data-edit-cart-item', true);
      }
      const waitListToogle = document.querySelector('.WaitlistModal__toggle');
      if (waitListToogle) {
        waitListToogle.innerHTML = '';
      }
    } else {
      const variantCents = String(variant.price).slice(-2);
      if (addToCartButton) addToCartButton.disabled = true;
      if (editCartItemButton) editCartItemButton.disabled = true;

      if (
        variant.option2 !== 'NS' &&
        ((variantCents !== '97' && variantCents !== '96' && variantCents !== '00' && variantCents !== '45') ||
          window.theme.country_iso !== 'US') &&
        window.hideWaitlistMetafields !== '1'
      ) {
        // Depending of the kind of button that is being used we will apply the correct changes to the elements
        if (addToCartButton) {
          addToCartButton.disabled = false;
          addToCartText.innerHTML = `<span class="waitlist-toggle">Sold Out <span class="ProductInfo__waitlist-text" data-product-id="${productJson.id}"  data-product-name="${productJson.title}" data-add-to-cart="false">/ Join the Waitlist</span></span>`;
          addToCartText.setAttribute('title', 'Sold Out / Join the Waitlist');

          addToCartButton.setAttribute('data-waitlist-open', true);
          addToCartButton.addEventListener('click', handleWaitlistClick);
          addToCartButton.setAttribute('data-add-to-cart', false);
        }
        if (editCartItemButton) {
          editCartItemButton.disabled = true;

          editCartItemText.innerHTML = theme.strings.soldOut;
          editCartItemText.setAttribute('title', 'Sold Out');

          editCartItemButton.setAttribute('data-waitlist-open', false);
          editCartItemButton.setAttribute('data-edit-cart-item', false);
          editCartItemButtonContainer.setAttribute('data-edit-cart-item', false);
        }
      } else {
        // Depending of the kind of button that is being used we will apply the correct changes to the elements
        if (addToCartButton) {
          addToCartText.innerHTML = theme.strings.soldOut;
          addToCartText.setAttribute('title', 'Sold Out');
          addToCartButton.setAttribute('data-waitlist-open', false);
          addToCartButton.setAttribute('data-add-to-cart', true);
        }
        if (editCartItemButton) {
          editCartItemText.innerHTML = theme.strings.soldOut;
          editCartItemText.setAttribute('title', 'Sold Out');
          editCartItemButton.setAttribute('data-waitlist-open', false);
          editCartItemButton.setAttribute('data-edit-cart-item', false);
          editCartItemButtonContainer.setAttribute('data-edit-cart-item', false);
        }
      }
    }

    document.querySelector('.Waitlist__always-open')?.setAttribute('data-waitlist-open', true);
    // Depending of the kind of button that is being used we will apply the correct changes to the elements
    if (addToCartButton) {
      document.querySelector('.Waitlist__always-open')?.setAttribute('data-add-to-cart', false);
    }
    if (editCartItemButton) {
      document.querySelector('.Waitlist__always-open')?.setAttribute('data-edit-cart-item', false);
    }
  },

  /**
   * Updates the DOM state of the add to cart button
   *
   * @param {boolean} enabled - Decides whether cart is enabled or disabled
   * @param {string} text - Updates the text notification content of the cart
   */
  updateAddToCartState(evt) {
    const variant = evt.detail.variant;
    const container = evt.currentTarget.closest('[data-section-type="product"]');
    const productJsonElement = container.querySelector(selectors.productJson);
    const productJson = productJsonElement ? JSON.parse(productJsonElement.innerHTML) : null;

    try {
      this.updateAddToCartButtonByExisting(variant);
    } catch (err) {
      console.error('PDP: Error in variant price and add to cart state. Error: ', err);
    }

    const newVariantName = variant.name;

    if (window.variant_name !== newVariantName) {
      const event = new CustomEvent('selectedProductDidUpdate', {
        detail: {
          variant: variant,
          container,
        },
      });
      document.dispatchEvent(event);
      window.variant_name = newVariantName;
    }

    // Yield these code executions to the end of the queue
    // they aren't user-visible tasks and can be deferred
    setTimeout(() => {
      try {
        this.updateAddToCartButtonByInventory(variant, productJson);

        document.querySelector('.QuickView__content button') &&
          document.querySelector('.QuickView__content button')?.setAttribute('data-location', 'quickview');
      } catch (err) {
        console.error('PDP: Error in product availability. Error: ', err);
      }

      // We only show or fire the waitlist modal if the quickview is opened in anywhere except the cart page
      const editCartItemButton = this.container.querySelector(selectors.editCartItem);
      if (!editCartItemButton) {
        this.updateJoinWaitlist(container, variant, productJson);
      }
    }, 0);
  },

  // ignore for international experience
  updateJoinWaitlist(container, variant, product) {
    const variants = product.variants;
    const variantColor = variant.option1;
    const waitlistModal = container.querySelector('.WaitlistModal');
    const variantCents = String(variant.price).slice(-2);
    let showWaitlistModal = false;

    let variantInventory = '';

    for (let i = 0; i < variants.length; i++) {
      if (window.inventory980) {
        variantInventory = window.inventory980.color[variants[i].id];
      } else {
        variantInventory = variants[i].available;
      }

      if (
        variants[i].option1 === variantColor &&
        variants[i].option2 !== 'NS' &&
        variantInventory == false &&
        ((variantCents !== '97' && variantCents !== '96' && variantCents !== '00' && variantCents !== '45') ||
          window.theme.country_iso !== 'US')
      ) {
        showWaitlistModal = true;
      }
    }

    if (showWaitlistModal && window.hideWaitlistMetafields !== '1') {
      waitlistModal.style.display = 'block';
    } else {
      waitlistModal.style.display = 'none';
    }
  },

  updateImages(evt) {
    const variant = evt.detail.variant;
    const imageId = variant.featured_image.id;

    this.switchImage(imageId);
    this.setActiveThumbnail(imageId);
  },

  /**
   * Updates the DOM with specified prices
   *
   * @param {string} productPrice - The current price of the product
   * @param {string} comparePrice - The original price of the product
   */
  updateProductPrices(evt) {
    const currencySymbol = window.theme.currency_symbol ? window.theme.currency_symbol : '$';
    const currencyIso = window.theme.currency_iso ? window.theme.currency_iso : 'USD';
    const currencyAlternateIso = currencySymbol === '$' && currencyIso !== 'USD' ? currencyIso : '';
    const variant = evt.detail.variant;
    const comparePrice = this.container.querySelector(selectors.comparePrice);
    const discountPercent = this.container.querySelector(selectors.discountPercent);
    const compareEls = [comparePrice, this.container.querySelector(selectors.comparePriceText)];
    const priceWrapper = this.container.querySelector(selectors.priceWrapper);
    let price;

    price = formatMoney(variant.price, theme.moneyFormat);

    this.container.querySelector(selectors.productPrice).innerHTML = `${currencyAlternateIso}${price}`;
    priceWrapper.setAttribute('aria-label', `Price: ${currencyAlternateIso}${price}`);

    try {
      if (variant.compare_at_price > variant.price) {
        comparePrice.innerHTML = formatMoney(variant.compare_at_price, theme.moneyFormat);
        const percentOff = (((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price) * 100 * 0.01;
        const percentOffRound = Math.round(percentOff);
        discountPercent.innerHTML = `<span class="ProductHeading__discount">${percentOffRound}% Off</span>`;
        priceWrapper.setAttribute(
          'aria-label',
          `Price: ${currencyAlternateIso}${price} Original price: ${currencyAlternateIso}${formatMoney(
            variant.compare_at_price,
            theme.moneyFormat,
          )}, ${percentOffRound}% Off`,
        );

        compareEls.forEach((el) => el.classList.remove(cssClasses.hide));
        if (this.container.querySelector('[data-product-price]')) {
          this.container.querySelector('[data-product-price]').classList.add('sale');
        }
        if (this.container.querySelector('[data-product-sticky-price]')) {
          this.container.querySelector('[data-product-sticky-price]').classList.add('ProductStickyBuy__price--on-sale');
        }
      } else {
        comparePrice.innerHTML = '';
        compareEls.forEach((el) => el.classList.add(cssClasses.hide));
        discountPercent.innerHTML = '';
        this.container.querySelector('[data-product-price]').classList.remove('sale');
        // TO-DO: [data-product-sticky-price] is deprecated but we will keep it for backward compatibility
        this.container
          .querySelector('[data-product-sticky-price]')
          ?.classList.remove('ProductStickyBuy__price--on-sale');
      }
    } catch (err) {
      console.log(err);
      console.log('PDP: Error in appending class sale');
    }
  },

  /**
   * Event callback for Theme Editor `section:unload` event
   */
  onUnload() {
    // Skip execution if we're in QuickView US experience (FastSimon)
    const quickViewModal = document.querySelector('#QuickView.Modal--open');
    const isBuiltWithFastSimon = quickViewModal?.getAttribute('data-built-with-fastsimon') === 'true';

    if (isBuiltWithFastSimon) {
      return;
    }

    this.container.removeEventListener('variantChange', this.updateAddToCartState.bind(this));
    this.container.removeEventListener('variantPriceChange', this.updateProductPrices.bind(this));
    if (this.featuredImage) {
      this.container.removeEventListener('variantImageChange', this.updateImages.bind(this));
    }
  },
});
